import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import type { AxiosError } from "axios"
import { Loader2 } from "lucide-react"

import type { IBulkAddTraitResult, ITrait } from "@apis/traits/types"
import { useBulkAddTraitMutation } from "@apis/traits/useTraits"
import { ConfirmDialog } from "@components/ConfirmDialog/ConfirmDialog"
import { Button } from "@components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select"
import { Textarea } from "@components/ui/textarea"

import { BulkAddTraitsSummary } from "./BulkAddTraitsSummary"
import {
  MAX_MANUAL_ACCOUNT_IDS,
  parseAccountIds,
} from "./utils/parseAccountIds"

type TManualUploadFormProps = {
  traits: ITrait[]
}

type TManualUploadFormData = {
  trait_code: string
  reason?: string
  account_ids: string
}

export const ManualUploadForm = ({ traits }: TManualUploadFormProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [result, setResult] = useState<IBulkAddTraitResult | null>(null)

  const form = useForm<TManualUploadFormData>({
    mode: "onChange",
    defaultValues: { trait_code: "", reason: "", account_ids: "" },
  })
  const bulkAddTraitMutation = useBulkAddTraitMutation()

  const accountIds = parseAccountIds(form.watch("account_ids"))
  const traitCode = form.watch("trait_code")
  const traitName =
    traits.find((trait) => trait.trait_code === traitCode)?.trait_name ??
    traitCode

  const handleConfirm = () => {
    const { trait_code, reason } = form.getValues()

    bulkAddTraitMutation.mutate(
      { trait_code, reason: reason || undefined, account_ids: accountIds },
      {
        onSuccess: (data) => {
          setResult(data)
          toast.success("Traits processed!")
          form.reset()
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error adding traits! ${error}`),
        onSettled: () => setIsConfirmOpen(false),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manual Upload</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(() => setIsConfirmOpen(true))}
            className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
          >
            <div className="tw:col-span-12 tw:md:col-span-6">
              <FormField
                control={form.control}
                name="trait_code"
                rules={{ required: "Trait is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trait</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Trait" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {traits.map((trait) => (
                          <SelectItem
                            key={trait.trait_code}
                            value={trait.trait_code}
                          >
                            {trait.trait_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12 tw:md:col-span-6">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason (optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Reason"
                        name={field.name}
                        value={field.value ?? ""}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12">
              <FormField
                control={form.control}
                name="account_ids"
                rules={{
                  validate: (value) => {
                    const parsedAccountIds = parseAccountIds(value)

                    if (parsedAccountIds.length === 0)
                      return "At least one account id is required"

                    if (parsedAccountIds.length > MAX_MANUAL_ACCOUNT_IDS)
                      return `A maximum of ${MAX_MANUAL_ACCOUNT_IDS} account ids can be processed at a time`

                    return true
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account IDs</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={1}
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormDescription>
                      Separated by any non-numeric character. Up to{" "}
                      {MAX_MANUAL_ACCOUNT_IDS} accounts at a time, use the file
                      upload for larger batches. {accountIds.length} account(s)
                      entered.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12 tw:flex tw:justify-end tw:gap-4">
              <Button type="submit" disabled={bulkAddTraitMutation.isPending}>
                {bulkAddTraitMutation.isPending && (
                  <Loader2 className="tw:animate-spin" />
                )}
                Add trait
              </Button>
            </div>
          </form>
        </Form>

        {result && <BulkAddTraitsSummary result={result} />}
      </CardContent>

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Add this trait to the selected accounts?"
        description={`This will add ${traitName} to ${accountIds.length} account(s).`}
        confirmLabel="Add trait"
        isPending={bulkAddTraitMutation.isPending}
        onConfirm={handleConfirm}
      />
    </Card>
  )
}
